import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// 列表页"筛选/页码 ↔ URL query"同步的公共实现 (2026-07-30).
// 用法:
//   const initQ = new URLSearchParams(useLocation().search);
//   const [keyword, setKeyword] = useState(initQ.get('keyword') || '');
//   const [page, setPage] = useState(Number(initQ.get('page')) || 1);
//   ...
//   useSyncQueryToUrl('/materials', { keyword, type1, page });
// 进详情后浏览器后退, 列表页从 URL 还原筛选和页码, 不再回到第 1 页.

/**
 * 把列表状态写回当前路由的 query (history.replace, 不产生新历史记录).
 * 规则: 空串/undefined/null 不写; key 为 "page" 且 <=1 不写 (默认第 1 页).
 * 特殊编码 (如 status 空值需区分"缺省") 由调用方在传入前算好.
 */
export function useSyncQueryToUrl(
    path: string,
    params: Record<string, string | number | undefined>,
) {
    const history = useHistory();
    // 序列化后做依赖, 避免调用方每次渲染传新对象导致 effect 空转
    const json = JSON.stringify(params);
    useEffect(() => {
        const entries = JSON.parse(json) as Record<string, string | number | undefined>;
        const q = new URLSearchParams();
        Object.entries(entries).forEach(([k, v]) => {
            if (v === undefined || v === null || v === '') return;
            if (k === 'page' && Number(v) <= 1) return;
            q.set(k, String(v));
        });
        const qs = q.toString();
        history.replace(`${path}${qs ? `?${qs}` : ''}`);
    }, [path, json, history]);
}

/**
 * 浏览器前进/后退 (POP) 时把当前 query 交给 onPop 还原页面状态.
 *
 * 同一路由下只改 query 不会重挂组件, 而列表页的状态只在挂载时从 URL 读一次 ——
 * 页内用 history.push 造了历史记录 (如统计图下钻到明细) 后, 不配这个钩子的话
 * 后退只会改地址栏, 页面还停在原来的状态.
 */
export function useRestoreQueryOnPop(onPop: (q: URLSearchParams) => void) {
    const history = useHistory();
    const { search } = useLocation();
    const cb = useRef(onPop);
    cb.current = onPop;
    useEffect(() => {
        if (history.action === 'POP') cb.current(new URLSearchParams(search));
    }, [search, history]);
}
